import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { hashKey } from "@tanstack/react-query";
import { DelimitedArrayParam, useQueryParam } from "use-query-params";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    GeneralLedgerView,
    GeneralLedgerViewSchema,
} from "@common/types/views/generalLedgerView";
import {
    getAccountTransactionsQueryKey,
    useAccountTransactionsQuery,
} from "@api/accountingReports.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useAccounts } from "@hooks/useAccounts";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useEntities } from "@hooks/useEntities";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { CollapseIcon, ExpandIcon } from "@icons";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { DateOnlyParam } from "@helpers/dateOnlyParam";
import { LedgerBasisSelect } from "@components/ledger/LedgerBasisSelect";
import { generalLedgerFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { useAccountingTabContext } from "../useAccountingTab";
import { GeneralLedgerPageContent } from "./GeneralLedgerPageContent";
import { useGeneralLedgerDownload } from "./useGeneralLedgerDownload";
import {
    countGeneralLedgerPostingRows,
    getGeneralLedgerDownloadMenuState,
    GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP,
} from "./generalLedgerDownloadMenu";
import { useGeneralLedgerLedger } from "./useGeneralLedgerLedger";
import { buildGLConfig, buildGLView } from "./glViewConfig";
import { useGeneralLedgerExpandedState } from "./useGeneralLedgerExpandedState";

export const GeneralLedgerPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis, setLedgerBasis } =
        useAccountingTabContext();
    const allEntities = useEntities();
    const { accounts } = useAccounts({ entityId: filters.entityId });
    const [selectedClassificationsRaw, setSelectedClassifications] =
        useQueryParam("classifications", DelimitedArrayParam);
    const [pnlBalanceSince] = useQueryParam("pnlBalanceSince", DateOnlyParam);

    const selectedClassifications = useMemo(
        () =>
            (selectedClassificationsRaw?.filter(Boolean) ?? undefined) as
                | AccountClass[]
                | undefined,
        [selectedClassificationsRaw],
    );

    useEffect(() => {
        if (!selectedClassifications?.length) {
            return;
        }
        const filteredAccounts = filters.accountCodes?.filter(Boolean);
        let selectedAccounts = new Set<number>();
        if (selectedClassifications) {
            selectedAccounts = new Set([
                ...accounts
                    .filter((account) =>
                        selectedClassifications.includes(account.class),
                    )
                    .map((account) => account.code),
                ...(filteredAccounts ?? []),
            ]);
        }
        const isNotEqual =
            selectedAccounts.size !== filters.accountCodes?.length ||
            filters.accountCodes?.some((code) => !selectedAccounts.has(code));

        if (isNotEqual) {
            setFilters((current) => ({
                ...current,
                accountCodes: Array.from(selectedAccounts),
            }));
            setSelectedClassifications([]);
        }
    }, [
        selectedClassifications,
        setSelectedClassifications,
        accounts,
        filters.accountCodes,
        setFilters,
    ]);

    const businessEntityIds = useBusinessNonMockEntityIds();

    const selectedEntityIds = useMemo(() => {
        if (filters.entityIds && filters.entityIds.length > 0) {
            return filters.entityIds;
        }
        if (selectedEntity) {
            return [selectedEntity.id];
        }
        return businessEntityIds;
    }, [filters.entityIds, selectedEntity, businessEntityIds]);

    const entitiesById = useMemo(
        () => new Map(allEntities.map((e) => [e.id, e])),
        [allEntities],
    );

    const handleLedgerIdResolved = useCallback(
        (ledgerId: string) =>
            setFilters((current) => ({ ...current, ledgerId })),
        [setFilters],
    );

    const {
        resolvedLedgerId,
        basisOptions,
        shouldRenderBasisSelect,
        isEntityDisabled,
    } = useGeneralLedgerLedger({
        selectedEntityIds,
        ledgerLookupEntityIds: businessEntityIds,
        ledgerId: filters.ledgerId,
        onLedgerIdResolved: handleLedgerIdResolved,
        preferredBasis: ledgerBasis,
        onBasisResolved: setLedgerBasis,
    });

    const clearedEntityIdsFallback = useMemo(() => {
        const businessIds = new Set(businessEntityIds);
        const validIds = allEntities
            .filter((entity) => businessIds.has(entity.id))
            .filter((entity) => !isEntityDisabled(entity))
            .map((entity) => entity.id);
        return validIds.length > 0 ? validIds : businessEntityIds;
    }, [allEntities, businessEntityIds, isEntityDisabled]);

    const reportFilters = useMemo(
        () => ({ ...filters, ledgerId: resolvedLedgerId }),
        [filters, resolvedLedgerId],
    );

    // Gated on resolvedLedgerId so the write lands after ledgers load
    useEffect(() => {
        if (selectedEntityIds.length !== 1 || !resolvedLedgerId) {
            return;
        }
        const [singleEntityId] = selectedEntityIds;
        if (filters.entityId === singleEntityId) {
            return;
        }
        setFilters((current) => ({
            ...current,
            entityId: singleEntityId,
        }));
    }, [selectedEntityIds, resolvedLedgerId, filters.entityId, setFilters]);

    const queryParams = useMemo(
        () => ({
            filters: reportFilters,
            pnlBalanceSince,
            entityIds: selectedEntityIds,
        }),
        [reportFilters, pnlBalanceSince, selectedEntityIds],
    );

    const isClassesEnabled = useIsClassesEnabled();
    const defaultView = useMemo(
        () => buildGLView({ isClassesEnabled }),
        [isClassesEnabled],
    );
    const { ledger, generationDate } = useAccountTransactionsQuery(queryParams);
    const numberFormat = ledger.data?.presentation.numberFormat;
    const glViewConfig = useMemo(
        () =>
            buildGLConfig({
                multiEntity: true,
                numberFormat,
                isClassesEnabled,
            }),
        [numberFormat, isClassesEnabled],
    );

    const { download, isDownloading } = useGeneralLedgerDownload({
        requestDto: queryParams,
        entityIds: selectedEntityIds,
        entitiesById,
        rowVisibility: ledger.data?.presentation?.rowVisibility,
    });
    const glData = useMemo(
        () => ledger.data?.accountsWithTransactions ?? [],
        [ledger.data?.accountsWithTransactions],
    );
    const downloadMenuState = useMemo(
        () =>
            getGeneralLedgerDownloadMenuState({
                isFetching: ledger.isFetching,
                isSuccess: ledger.isSuccess,
                postingRowCount: countGeneralLedgerPostingRows(glData),
            }),
        [ledger.isFetching, ledger.isSuccess, glData],
    );
    const reportKey = useMemo(
        () => hashKey(getAccountTransactionsQueryKey(queryParams)),
        [queryParams],
    );
    const { expanded, setExpanded, isCollapsed, toggleAll } =
        useGeneralLedgerExpandedState({
            data: glData,
            reportKey,
            isReportLoaded: ledger.isSuccess && !ledger.isPlaceholderData,
        });

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<GeneralLedgerView>(
            WORKSPACE_USER_CACHE_KEYS.GENERAL_LEDGER_PAGE_VIEW_CACHE_KEY,
            GeneralLedgerViewSchema,
        );

    const [view, setView] = useState<GeneralLedgerView>(
        cachedView ?? defaultView,
    );

    const handleViewChange = useCallback(
        (newView: GeneralLedgerView) => {
            setView(newView);
            setCachedView(newView);
        },
        [setCachedView],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_GENERAL_LEDGER_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            <AccountingReportViewToolbar
                reportId="generalLedger"
                filters={filters}
                setFilters={setFilters}
                viewConfig={glViewConfig}
                glLayout={{
                    view,
                    onViewChange: handleViewChange,
                    defaultView,
                }}
            >
                <Tooltip
                    key={isCollapsed ? "expand" : "collapse"}
                    content={isCollapsed ? "Expand all" : "Collapse all"}
                >
                    <Button
                        iconOnly
                        variant="primary"
                        size="md"
                        onClick={toggleAll}
                        aria-label={isCollapsed ? "Expand all" : "Collapse all"}
                    >
                        {isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
                    </Button>
                </Tooltip>
                <DownloadDropdown
                    onDownload={(format) => {
                        if (selectedEntityIds.length === 0) {
                            return;
                        }
                        download({
                            format,
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                        });
                    }}
                    disabled={downloadMenuState.menuDisabled}
                    loading={isDownloading}
                    pdfDisabled={downloadMenuState.pdfDisabled}
                    pdfDisabledTooltip={GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP}
                />
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                ledgerSelectOverride={
                    shouldRenderBasisSelect ? (
                        <LedgerBasisSelect
                            options={basisOptions}
                            value={resolvedLedgerId}
                            onChange={handleLedgerIdResolved}
                        />
                    ) : undefined
                }
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        isEntityDisabled={isEntityDisabled}
                        disabledEntityTooltip="This entity has no accrual ledger."
                        onChange={(newFilters) =>
                            setFilters((current) => ({
                                ...current,
                                // API rejects empty arrays.
                                entityIds:
                                    newFilters.entityIds &&
                                    newFilters.entityIds.length > 0
                                        ? newFilters.entityIds
                                        : clearedEntityIdsFallback,
                            }))
                        }
                    />
                }
                {...generalLedgerFilterDefinition.filterProps}
                searchPlaceholder="Search descriptions..."
            />
            {selectedEntityIds.length > 0 && (
                <LoaderOnInitialLoading queryResult={ledger} dimWhileFetching>
                    {ledger.data && (
                        <GeneralLedgerPageContent
                            data={ledger.data.accountsWithTransactions}
                            filters={reportFilters}
                            chrome={ledger.data.presentation.chrome}
                            numberFormat={ledger.data.presentation.numberFormat}
                            generationDate={generationDate}
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            view={view}
                            onViewChange={handleViewChange}
                            viewConfig={glViewConfig}
                        />
                    )}
                </LoaderOnInitialLoading>
            )}
            <Outlet />
        </>
    );
};
